import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Service from '@ember/service';
import type PartnerService from './partner';
import type SessionContextService from './session-context';
import type { Store as StoreService } from '@warp-drive/core';
import type { EmployeePaymentHistory as EmployeePaymentHistoryModel } from 'tio-data/data/resources/employee-payment-history.type';
import { employeePaymentHistoryQuery } from 'tio-data/requests/employee-payment-history';
import {
  accountingPaymentsRequest,
  accountingPreviewRequest,
} from 'tio-data/requests/employee-actions';
import { getSlrAlertsRequest, parseSlrAlerts } from 'tio-data/requests/person';
import { shouldShowSlrLoanStandingAlert } from 'tio-employee/utils/slr-loan-standing-verification';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type MemberResolutionsService from 'tio-employee/services/member-resolutions';
import { toResolutionAlert } from 'tio-employee/utils/slr/resolution-alert';

export interface PastContributionPayment {
  amount: number;
  line_item_date: string;
  line_item_period: string;
  line_item_type: string;
  transaction_date?: string | null;
  servicer: string;
}

/**
 * Raw JSON returned by GET /employees/{id}/accounting_payments. Replaces the
 * untyped result the legacy `getAccountingPaymentContributions` adapter method
 * returned; the getters below read `.payments` / `.summary` off it.
 */
export interface AccountingPaymentContributions {
  payments?: PastContributionPayment[];
  summary?: { total: number };
}

export enum PaymentValidationErrorKey {
  ACCOUNTS_UNPAYABLE = 'ACCOUNTS_UNPAYABLE',
  NO_LOAN_BALANCE = 'NO_LOAN_BALANCE',
  EXCEEDED_LIMIT = 'EXCEEDED_LIMIT',
  NOT_STARTED_PLAN = 'NOT_STARTED_PLAN',
  NO_PLAN_CONTRIBUTION = 'NO_PLAN_CONTRIBUTION',
  INVALID_LOAN_TYPE = 'INVALID_LOAN_TYPE',
  AGGREGATION_INVALID_LOAN_TYPE = 'AGGREGATION_INVALID_LOAN_TYPE',
  NO_EMPLOYEE_TRANSACTIONS = 'NO_EMPLOYEE_TRANSACTIONS',
  TERMS_NOT_ACCEPTED = 'TERMS_NOT_ACCEPTED',
  STALE_ACCOUNT = 'STALE_ACCOUNT',
  NAME_MISMATCH = 'NAME_MISMATCH',
  INVALID_LOAN_STATUS = 'INVALID_LOAN_STATUS',
}

export interface PaymentValidationError {
  key: PaymentValidationErrorKey;
  message: string;
  source: string;
}

export interface FutureContributionPayment {
  amount: number;
  date: string;
  state: string;
  period: string;
  validation_errors: PaymentValidationError[];
}

export default class EmployeeService extends Service {
  @service declare partner: PartnerService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;
  @service('member-resolutions') declare memberResolutions: MemberResolutionsService;

  get employee() {
    return this.sessionContext.currentEmployee;
  }

  get current() {
    return this.sessionContext.currentEmployee;
  }

  get mergedAdditionFields() {
    return this.employee?.employeeAddition?.editableMergedFields;
  }

  get contributionPayments(): PastContributionPayment[] {
    return this.contributionsState.value?.payments || [];
  }

  get contributionSummary() {
    return this.contributionsState.value?.summary;
  }

  get futureContributionPayments(): FutureContributionPayment[] {
    return this.futurePayments.value || [];
  }

  get hasScholarships() {
    const scholarships = this.employee?.scholarships;

    if (scholarships?.length) {
      const validScholarship = scholarships.find((scholarship) => {
        if (!scholarship.thruDate) {
          return true;
        }
        const thruDate = new Date(scholarship.thruDate).getTime();
        const nowDate = new Date().getTime();
        return thruDate > nowDate;
      });
      return !!validScholarship;
    }

    return false;
  }

  get hasContributions() {
    return !!this.contributionPayments.length;
  }

  get isTaOnlyEmployee() {
    const companyKey = this.employee?.plan?.companyKey ?? '';
    const taOnlyPlanId = this.partner.companySettings?.tuitionAssistance?.taOnlyPlanId ?? '';
    return !!(taOnlyPlanId && companyKey.includes(taOnlyPlanId));
  }

  get isLoadedEmployee() {
    return !!this.employee?.id;
  }

  get needsToLinkLoans() {
    return !this.employee.needsToLinkLoans;
  }

  get hasRogueFein() {
    return this.employee.pslfProfile?.feinIsRogue;
  }

  // ── JSONAPI Query Methods ──

  /**
   * Fetches the employee's combined payment history (accounting transactions
   * unioned with paid SLR external-benefit usages) from the v2 kit endpoint,
   * with pagination and sorting. Canceled and failed rows are excluded
   * server-side; meta carries the kit's page and summary members.
   */
  async queryEmployeePaymentHistories(options?: {
    page?: { number?: number; size?: number };
    sort?: string;
  }) {
    const employee = this.employee;
    if (!employee?.id) {
      return { rows: [] as EmployeePaymentHistoryModel[], meta: {} };
    }

    const { content } = (await this.store.request(
      employeePaymentHistoryQuery(employee.id, {
        page: { number: options?.page?.number ?? 1, size: options?.page?.size ?? 6 },
        sort: options?.sort || '-payment-date',
      })
    )) as { content: { data: EmployeePaymentHistoryModel[]; meta?: Record<string, unknown> } };

    return {
      rows: content.data,
      meta: content.meta || {},
    };
  }

  // ── Tracked Functions ──

  contributionsState = trackedFunction(this, async () => {
    // currentEmployee is an unsaved placeholder, never null, for a member with no employment.
    const employeeId = this.employee?.id;
    if (!employeeId) {
      return;
    }
    try {
      const response = await this.store.request(accountingPaymentsRequest(employeeId));
      return response.content as AccountingPaymentContributions;
    } catch (e) {
      console.warn(e);
    }
  });

  futurePayments = trackedFunction(this, async () => {
    const employee = this.employee;
    if (employee.plan && employee.plan.cadence !== 'NEVER') {
      try {
        const response = await this.store.request(accountingPreviewRequest(employee.id!));
        return response.content as FutureContributionPayment[];
      } catch (e) {
        console.warn(e);
      }
    } else {
      return;
    }
  });

  /**
   * Reads receiving-account alerts from /people/:id/slr-alerts.
   * Undefined while loading, [] on failure: a request failure must not mark the dashboard account as blocked.
   */
  slrAlertsData = trackedFunction(this, async () => {
    // No SLR plan → nothing to fetch (mirrors the futurePayments guard);
    // skipping the request avoids pointless /slr-alerts traffic for every
    // non-SLR employee who lands on the dashboard.
    const employee = this.employee;
    if (!employee?.plan || employee.plan.cadence === 'NEVER') {
      return [];
    }
    // The session USER's person, not employee.person: the API's slr_alerts
    // policy authorizes `user.person == record`, and the other /slr-alerts
    // callers already resolve the id this way.
    const personId = this.sessionContext.person?.id;
    if (!personId) {
      return [];
    }
    try {
      const { content } = await this.store.request(getSlrAlertsRequest(personId));

      return parseSlrAlerts(content).alerts;
    } catch (e) {
      console.warn('[employee] Failed to fetch SLR alerts', e);
      return [];
    }
  });

  // A question the member answered or dismissed on this page session asks nothing more.
  get openSlrAlerts() {
    return (this.slrAlertsData.value ?? []).filter((alert) => {
      const question = toResolutionAlert(alert);
      return !question || this.memberResolutions.isOpen(question);
    });
  }

  get hasSlrAccountAlerts() {
    return this.openSlrAlerts.length > 0;
  }

  /** Whether an open alert stops the next contribution; a server that omits the flag means it does. */
  get hasBlockingSlrAlerts() {
    return this.openSlrAlerts.some((alert) => alert.blocks_contribution !== false);
  }

  /**
   * The loan-standing verification audit state for the receiving account —
   * the dashboard tile's second error source (the "Can't Process Payment"
   * state, which the SLR alerts spec assigns to every audit-window row,
   * alongside /slr-alerts). Undefined while loading,
   * null on failure — a fetch error must not flip the tile into its error
   * state.
   */
  slrLoanStandingData = trackedFunction(this, async ({ isRetrying }) => {
    const employee = this.employee;
    if (!employee?.plan || employee.plan.cadence === 'NEVER') return null;
    try {
      // reload on retry — fetchCurrentState
      // is a plain query() whose cached document survives the SkipCache writes
      // that changed it (see its own comment at the `reload` param).
      return await this.slrLoanStandingVerification.fetchCurrentState({ reload: isRetrying });
    } catch (e) {
      console.warn('[employee] Failed to fetch SLR loan-standing state', e);
      return null;
    }
  });

  get hasSlrLoanStandingAttention() {
    return shouldShowSlrLoanStandingAlert(this.slrLoanStandingData.value);
  }

  /**
   * Refreshes every SLR read after an upload or account change: the verdict reloads, alerts skip the cache.
   */
  refreshSlrState() {
    void this.slrAlertsData.retry();
    void this.slrLoanStandingData.retry();
  }
}
